import partition from 'lodash/partition';

const wrapOption = (customData, valueGetter) => option => {
  // Each option has:
  // 1. value - That's the value corresponding to each option.
  // 2. original - The original option object, UNHARMED!
  // 3. customData - an object containing custom data about this specific option.
  //                 The custom data always contains the index of the option in the resulting array.
  //                 It can also contain the group of the item if we're in grouped mode.
  return {
    customData,
    value: valueGetter(option),
    original: option
  };
};

const reduceGroups = (options, cb, ...rest) => {
  return options.reduce(
    (acc, group, index, ...reduceRest) =>
      cb(
        acc,
        {
          items: group[1],
          header: group[0],
          id: `__SelectDropdown__group__id__${index}__${group[0]}`
        },
        index,
        ...reduceRest
      ),
    ...rest
  );
};

const partitionBase = (options, partitionBy, valueGetter, wrapperCustomData) =>
  partition(
    options.map(wrapOption(wrapperCustomData, valueGetter)),
    option => partitionBy(option) !== undefined
  );

const partitionOptions = (options, partitionBy, valueGetter) => {
  const [firstPart, secondPart] = partitionBase(options, partitionBy, valueGetter);
  return [...firstPart, ...secondPart];
};

const partitionGroups = (options, partitionBy, valueGetter) => {
  const { selected, unselected } = reduceGroups(
    options,
    (acc, group) => {
      const [firstPart, secondPart] = partitionBase(group.items, partitionBy, valueGetter, {
        group
      });

      acc.selected = acc.selected.concat(firstPart);
      acc.unselected = acc.unselected.concat(secondPart);

      return acc;
    },
    { selected: [], unselected: []}
  );

  return [...selected, ...unselected];
};

const prefixWithOptions = (prefixOptions, options, valueGetter, wrapperCustomData) => {
  return prefixOptions
    .map(wrapOption(null, valueGetter))
    .concat(options)
    .map(wrapOption(wrapperCustomData, valueGetter));
};

const prefixWithGroupOptions = (prefixOptions, options, valueGetter) => {
  return reduceGroups(
    options,
    (acc, group) => acc.concat(group.items.map(wrapOption({ group }, valueGetter))),
    prefixOptions.map(
      wrapOption({ group: { items: prefixOptions, index: 0, isPrefix: true }}, valueGetter)
    )
  );
};

export default ({
  options = [],
  prefixOptions = [],
  selectedValues = {},
  valueGetter,
  grouped,
  multi
}) => {
  const partitionBySelectedValues = option => selectedValues[valueGetter(option.original)];

  const partitionMethod = grouped ? partitionGroups : partitionOptions;
  const prefixMethod = grouped ? prefixWithGroupOptions : prefixWithOptions;

  const result = multi
    ? partitionMethod(options, partitionBySelectedValues, valueGetter)
    : prefixMethod(prefixOptions, options, valueGetter);

  // For the structure of each option look at the wrapOption method.
  return result.map((option, index) => ({
    ...option,
    customData: {
      ...option.customData,
      index
    }
  }));
};
